import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { inventoryApi } from '@/lib/inventoryApi';
import { parseTypedAmount, toNumber } from '@/lib/number';
import { showSuccess, showError } from '@/lib/apiErrors';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { X, ArrowDownCircle, Package } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { ReceiveStockRequest, InventoryItem } from '@/types/inventory';
import type { ApiListResponse } from '@/types/api';

interface ReceiveStockDrawerProps {
  open: boolean;
  onClose: () => void;
  farmId: string;
  preselectedItemId?: string;
  onSuccess?: () => void;
}

export function ReceiveStockDrawer({
  open,
  onClose,
  farmId,
  preselectedItemId,
  onSuccess,
}: ReceiveStockDrawerProps) {
  return (
    <>
      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 bg-black/25 backdrop-blur-sm z-40 transition-opacity"
          onClick={onClose}
        />
      )}

      {/* Drawer panel */}
      <div
        className={cn(
          'fixed inset-y-0 right-0 z-50 w-full sm:w-[440px]',
          'bg-white shadow-xl border-l border-gray-200',
          'transform transition-transform duration-300 ease-out',
          open ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        {/* Unmount form when closed so state resets on reopen */}
        {open && (
          <ReceiveStockForm
            farmId={farmId}
            preselectedItemId={preselectedItemId}
            onClose={onClose}
            onSuccess={onSuccess}
          />
        )}
      </div>
    </>
  );
}

// Inner form component that resets via key remounting
interface ReceiveStockFormProps {
  farmId: string;
  preselectedItemId?: string;
  onClose: () => void;
  onSuccess?: () => void;
}

function ReceiveStockForm({
  farmId,
  preselectedItemId,
  onClose,
  onSuccess,
}: ReceiveStockFormProps) {
  // Form state (initialized fresh on each mount)
  const [itemId, setItemId] = useState(preselectedItemId ?? '');
  const [quantity, setQuantity] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [supplier, setSupplier] = useState('');
  const [batchNumber, setBatchNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [notes, setNotes] = useState('');

  // Fetch items for dropdown
  const { data: itemsData, isLoading: itemsLoading } = useQuery<ApiListResponse<InventoryItem>>({
    queryKey: ['inventory', 'items', farmId, 'active'],
    queryFn: () => inventoryApi.listItems(farmId, { is_active: 'true', include_stock: 'true' }),
    staleTime: 60 * 1000,
  });

  const items = itemsData?.data ?? [];
  const selectedItem = items.find((i) => i.id === itemId);
  const currentStock = selectedItem?.stock ? toNumber(selectedItem.stock.quantity) : 0;
  // Typed text: "12,5" is 12.5, "12,500" is 12500, anything else is refused with a message.
  const quantityParsed = parseTypedAmount(quantity, { thousands: true });
  const unitCostParsed = parseTypedAmount(unitCost, { thousands: true });
  const quantityValue = 'value' in quantityParsed ? quantityParsed.value : null;
  const unitCostValue = 'value' in unitCostParsed ? unitCostParsed.value : null;
  const projectedStock = currentStock + (quantityValue ?? 0);

  function handleItemChange(newItemId: string) {
    setItemId(newItemId);
    setUnitCost('');
    // Pre-fill unit cost from selected item
    const item = items.find((i) => i.id === newItemId);
    if (item?.unit_cost != null) {
      setUnitCost(String(toNumber(item.unit_cost)));
    }
  }

  // Submit mutation
  const mutation = useMutation({
    mutationFn: (data: ReceiveStockRequest) => inventoryApi.receiveStock(farmId, data),
    onSuccess: () => {
      showSuccess('Stock received successfully');
      onSuccess?.();
      onClose();
    },
    onError: (err) => {
      showError(err instanceof Error ? err.message : 'Failed to receive stock');
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!itemId || quantityValue === null || 'error' in unitCostParsed) return;

    const data: ReceiveStockRequest = {
      item_id: itemId,
      quantity: quantityValue,
      ...(unitCostValue !== null && { unit_cost: unitCostValue }),
      ...(supplier.trim() && { supplier: supplier.trim() }),
      ...(batchNumber.trim() && { batch_number: batchNumber.trim() }),
      ...(expiryDate && { expiry_date: expiryDate }),
      ...(notes.trim() && { notes: notes.trim() }),
    };

    mutation.mutate(data);
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-green-50 p-2">
            <ArrowDownCircle className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Receive Stock</h2>
            <p className="text-sm text-gray-500">Record incoming inventory</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Body */}
      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
        {/* Item selector */}
        {itemsLoading ? (
          <div className="flex items-center gap-2 py-4">
            <LoadingSpinner size={16} />
            <span className="text-sm text-gray-500">Loading items...</span>
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center py-6 text-center">
            <Package className="h-8 w-8 text-gray-300 mb-2" />
            <p className="text-sm text-gray-500">No active items. Create items first.</p>
          </div>
        ) : (
          <Select
            label="Item"
            value={itemId}
            onChange={(e) => handleItemChange(e.target.value)}
            placeholder="Select an item..."
            options={items.map((item) => ({
              value: item.id,
              label: `${item.name} (${item.unit})`,
            }))}
          />
        )}

        {/* Current stock indicator */}
        {selectedItem && (
          <div className="rounded-lg bg-gray-50 p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Current Stock</span>
              <span className="font-medium text-gray-900">
                {currentStock.toLocaleString()} {selectedItem.unit}
              </span>
            </div>
            {quantityValue !== null && quantityValue > 0 && (
              <div className="flex items-center justify-between text-sm mt-1">
                <span className="text-gray-500">After Receipt</span>
                <span className="font-medium text-green-600">
                  {projectedStock.toLocaleString()} {selectedItem.unit}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Quantity */}
        <Input
          label="Quantity"
          type="text"
          inputMode="decimal"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          error={'error' in quantityParsed ? quantityParsed.error : undefined}
          placeholder="0"
          required
        />

        {/* Unit cost */}
        <Input
          label="Unit Cost (PHP)"
          type="text"
          inputMode="decimal"
          value={unitCost}
          onChange={(e) => setUnitCost(e.target.value)}
          error={'error' in unitCostParsed ? unitCostParsed.error : undefined}
          placeholder="0.00"
          description="Cost per unit for this batch"
        />

        {/* Supplier */}
        <Input
          label="Supplier"
          value={supplier}
          onChange={(e) => setSupplier(e.target.value)}
          placeholder="e.g., CP Foods, Local dealer"
        />

        {/* Batch number */}
        <Input
          label="Batch Number"
          value={batchNumber}
          onChange={(e) => setBatchNumber(e.target.value)}
          placeholder="Optional batch/lot number"
        />

        {/* Expiry date */}
        <Input
          label="Expiry Date"
          type="date"
          value={expiryDate}
          onChange={(e) => setExpiryDate(e.target.value)}
        />

        {/* Notes */}
        <Textarea
          label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Additional notes..."
          rows={3}
        />
      </form>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex items-center gap-3">
        <Button
          variant="primary"
          fullWidth
          loading={mutation.isPending}
          disabled={!itemId || quantityValue === null || quantityValue <= 0 || 'error' in unitCostParsed}
          onClick={handleSubmit as unknown as () => void}
        >
          Receive Stock
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
